import toast from "react-hot-toast";

/**
 * Toast Helper
 * Centralized toast notification functions using react-hot-toast
 * Uses default library styling for consistency
 */

/**
 * Show success toast notification
 * @param {string} message - Success message to display
 * @param {Object} options - Optional toast configuration
 */
const showSuccess = (message, options = {}) => {
    return toast.success(message, options);
};

/**
 * Show error toast notification
 * @param {string|Object} error - Error message or error object
 * @param {Object} options - Optional toast configuration
 */
const showError = (error, options = {}) => {
    // Extract error message if error is an object
    let errorMessage = "حدث خطأ غير متوقع";

    if (typeof error === "string") {
        errorMessage = error;
    } else if (error && typeof error === "object") {
        errorMessage = error?.msg || error?.message || error?.title || error?.error || "حدث خطأ غير متوقع";
    }

    return toast.error(errorMessage, options);
};

/**
 * Show warning toast notification
 * @param {string} message - Warning message to display
 * @param {Object} options - Optional toast configuration
 */
const showWarning = (message, options = {}) => {
    return toast(message, {
        icon: "⚠️",
        ...options,
    });
};

/**
 * Show info toast notification
 * @param {string} message - Info message to display
 * @param {Object} options - Optional toast configuration
 */
const showInfo = (message, options = {}) => {
    return toast(message, {
        icon: "ℹ️",
        ...options,
    });
};

/**
 * Show loading toast notification
 * @param {string} message - Loading message to display
 * @param {Object} options - Optional toast configuration
 * @returns {string} Toast ID that can be used to dismiss or update the toast
 */
const showLoading = (message, options = {}) => {
    return toast.loading(message, options);
};

/**
 * Show promise toast notification
 * Automatically shows loading, success, or error based on promise state
 * @param {Promise} promise - Promise to track
 * @param {Object} messages - Messages for different states { loading, success, error }
 * @param {Object} options - Optional toast configuration
 */
const showPromise = (promise, messages, options = {}) => {
    return toast.promise(
        promise,
        {
            loading: messages.loading || "جاري التحميل...",
            success: messages.success || "تم بنجاح",
            error: (err) => {
                // Extract error message if error is an object
                if (typeof err === "string") return err;
                return err?.msg || err?.message || err?.title || messages.error || "حدث خطأ";
            },
        },
        options
    );
};

/**
 * Dismiss a specific toast or all toasts
 * @param {string} toastId - Optional toast ID to dismiss. If not provided, dismisses all toasts
 */
const dismiss = (toastId) => {
    if (toastId) {
        toast.dismiss(toastId);
    } else {
        toast.dismiss();
    }
};

/**
 * Custom toast with full control
 * @param {string} message - Message to display
 * @param {Object} options - Toast configuration
 */
const showCustom = (message, options = {}) => {
    return toast(message, options);
};

/**
 * Toast Helper Object
 * Exports all toast utility functions
 */
export const toastHelper = {
    success: showSuccess,
    error: showError,
    warning: showWarning,
    info: showInfo,
    loading: showLoading,
    promise: showPromise,
    dismiss: dismiss,
    custom: showCustom,
};

export default toastHelper;
